// Mock data for Krypton9 landing page

export const translations = {
  en: {
    nav: {
      services: "Services",
      about: "About",
      resources: "Resources",
      contact: "Contact",
      schedule: "Schedule Demo"
    },
    hero: {
      title: "Your business, intelligently amplified.",
      subtitle: "An intelligent layer on top of everything you do. No complexity, no jargon — just AI that works.",
      cta: "Schedule Consultation",
      secondary: "Download Guide"
    },
    services: {
      title: "What Changes When You Work With Us",
      subtitle: "Forget tools and tech jargon. This is about what happens to your business.",
      phases: [
        {
          title: "Understand",
          tagline: "See your business like never before",
          description: "Imagine opening your laptop and instantly knowing where your money goes, which customers are at risk, and where your biggest opportunities are hiding. No spreadsheets, no guesswork — just clarity.",
          benefits: ["Crystal-clear financial insight", "Hidden patterns revealed", "Real-time business health at a glance"],
          icon: "eye"
        },
        {
          title: "Decide",
          tagline: "Make decisions with the confidence of a Fortune 500 CEO",
          description: "What if you could test a risky decision before making it? Simulate market changes, forecast cash flow, and compare scenarios — all before committing a single euro.",
          benefits: ["Risk-free scenario testing", "Data-backed forecasting", "The end of gut-instinct decisions"],
          icon: "compass"
        },
        {
          title: "Automate",
          tagline: "Let AI handle the work that drains you",
          description: "That mountain of invoices, those repetitive reports, screening 200 CVs for one position — done. Not in hours. In seconds. While you focus on what actually grows your business.",
          benefits: ["Hours back in your week", "Zero human-error on routine tasks", "Your team freed for creative work"],
          icon: "sparkles"
        },
        {
          title: "Grow",
          tagline: "Scale without scaling your headcount",
          description: "The real magic? AI that works alongside you 24/7. An assistant that knows your business, spots threats before they become problems, and finds opportunities you'd never see alone.",
          benefits: ["An AI partner that never sleeps", "Proactive alerts and suggestions", "Growth without growing pains"],
          icon: "rocket"
        }
      ],
      industries: {
        title: "Built for Every Business",
        subtitle: "Whether you run a construction company or a flower shop — AI transforms the way you work.",
        items: [
          { name: "Retail & E-commerce", example: "Predict demand, personalize customer journeys, automate inventory" },
          { name: "Construction & Engineering", example: "Faster proposals, smarter project planning, material cost optimization" },
          { name: "Professional Services", example: "Client insights, automated reporting, intelligent scheduling" },
          { name: "Healthcare & Wellness", example: "Patient flow optimization, administrative automation, compliance monitoring" },
          { name: "Manufacturing", example: "Quality prediction, supply chain intelligence, maintenance forecasting" },
          { name: "Hospitality & Catering", example: "Occupancy forecasting, dynamic pricing, guest satisfaction analysis" },
          { name: "Transport & Logistics", example: "Route optimization, fleet management, real-time delivery tracking" },
          { name: "Agriculture & Food", example: "Harvest forecasting, supply chain optimization, quality assurance and traceability" }
        ]
      }
    },
    about: {
      title: "About Krypton9",
      subtitle: "Your AI Transformation Partner",
      description: "Krypton9 specializes in bringing cutting-edge Agentic AI solutions to all SME businesses. We understand the unique challenges of the SME sector and provide practical, profitable AI strategies that work.",
      stats: [
        { value: "50+", label: "Companies Transformed" },
        { value: "€2M+", label: "Cost Savings Generated" },
        { value: "95%", label: "Client Satisfaction" }
      ]
    },
    resources: {
      title: "Free Resources",
      subtitle: "Download our guides and whitepapers",
      items: [
        {
          title: "AI Strategy Guide for MKB",
          description: "Complete guide to developing your AI strategy",
          format: "PDF",
          file: "/api/static/resources/ai_strategy_guide_en.pdf"
        },
        {
          title: "ROI Calculator",
          description: "Calculate your potential AI investment returns",
          format: "Excel",
          file: "/api/static/resources/roi_calculator_en.xlsx"
        },
        {
          title: "Case Studies Collection",
          description: "Real success stories from Dutch companies",
          format: "PDF",
          file: "/api/static/resources/case_studies_en.pdf"
        }
      ]
    },
    contact: {
      title: "Let's Talk About Your AI Journey",
      subtitle: "Schedule a free consultation to discuss how AI can transform your business",
      form: {
        name: "Full Name",
        email: "Email Address",
        company: "Company Name",
        phone: "Phone Number",
        message: "Tell us about your needs",
        submit: "Schedule Consultation",
        success: "Thank you! We'll contact you within 24 hours.",
        error: "Something went wrong. Please try again."
      }
    },
    newsletter: {
      title: "Stay Updated",
      subtitle: "Get the latest AI insights and industry news",
      placeholder: "Enter your email",
      button: "Subscribe",
      success: "Successfully subscribed!",
      error: "Please enter a valid email"
    },
    footer: {
      tagline: "Transforming SME's with Agentic AI",
      links: {
        privacy: "Privacy Policy",
        terms: "Terms of Service",
        cookies: "Cookie Policy"
      },
      copyright: "© 2025 Krypton9. All rights reserved."
    },
    chat: {
      greeting: "Hi! How can we help you today?",
      placeholder: "Type your message..."
    },
    auth: {
      welcomeBack: "Welcome Back",
      signInSubtitle: "Sign in to access your portal",
      emailLabel: "Email Address",
      emailPlaceholder: "you@company.com",
      passwordLabel: "Password",
      signIn: "Sign In",
      signingIn: "Signing In...",
      noAccount: "Don't have an account?",
      createAccount: "Create Account",
      createTitle: "Create Your Account",
      createSubtitle: "Start your AI transformation journey",
      fullNameLabel: "Full Name",
      fullNamePlaceholder: "John Doe",
      companyLabel: "Company Name",
      companyPlaceholder: "Your Company B.V.",
      creatingAccount: "Creating Account...",
      alreadyAccount: "Already have an account?",
      loginFailed: "Login failed",
      registerFailed: "Registration failed"
    }
  },
  nl: {
    nav: {
      services: "Diensten",
      about: "Over Ons",
      resources: "Bronnen",
      contact: "Contact",
      schedule: "Plan Demo"
    },
    hero: {
      title: "Jouw bedrijf, intelligent versterkt.",
      subtitle: "Een intelligente laag bovenop alles wat je doet. Geen complexiteit, geen jargon — gewoon AI die werkt.",
      cta: "Plan Consultatie",
      secondary: "Download Gids"
    },
    services: {
      title: "Wat Verandert Er Als Je Met Ons Werkt",
      subtitle: "Vergeet tools en technisch jargon. Dit gaat over wat er met jouw bedrijf gebeurt.",
      phases: [
        {
          title: "Inzicht",
          tagline: "Zie je bedrijf zoals nooit tevoren",
          description: "Stel je voor: je opent je laptop en weet direct waar je geld naartoe gaat, welke klanten dreigen af te haken, en waar je grootste kansen liggen. Geen spreadsheets, geen giswerk — alleen helderheid.",
          benefits: ["Glashelder financieel overzicht", "Verborgen patronen ontdekt", "Realtime bedrijfsgezondheid in een oogopslag"],
          icon: "eye"
        },
        {
          title: "Beslissen",
          tagline: "Neem beslissingen met het vertrouwen van een Fortune 500 CEO",
          description: "Wat als je een riskante beslissing kon testen voordat je hem neemt? Simuleer marktveranderingen, voorspel cashflow en vergelijk scenario's — allemaal zonder een euro te riskeren.",
          benefits: ["Risicovrij scenario's testen", "Voorspellingen op basis van data", "Het einde van onderbuikgevoel-beslissingen"],
          icon: "compass"
        },
        {
          title: "Automatiseren",
          tagline: "Laat AI het werk doen dat je energie vreet",
          description: "Die berg facturen, die repetitieve rapporten, 200 CV's screenen voor een vacature — klaar. Niet in uren. In seconden. Terwijl jij je focust op wat je bedrijf echt laat groeien.",
          benefits: ["Uren terug in je week", "Nul fouten op routinetaken", "Je team vrij voor creatief werk"],
          icon: "sparkles"
        },
        {
          title: "Groeien",
          tagline: "Schaal op zonder je personeelsbestand te verdubbelen",
          description: "De echte magie? AI die 24/7 naast je werkt. Een assistent die je bedrijf kent, bedreigingen ziet voordat het problemen worden, en kansen vindt die je alleen nooit zou zien.",
          benefits: ["Een AI-partner die nooit slaapt", "Proactieve alerts en suggesties", "Groei zonder groeipijnen"],
          icon: "rocket"
        }
      ],
      industries: {
        title: "Gebouwd Voor Elk Bedrijf",
        subtitle: "Of je nu een bouwbedrijf runt of een bloemenwinkel — AI transformeert hoe je werkt.",
        items: [
          { name: "Retail & E-commerce", example: "Voorspel vraag, personaliseer klantreizen, automatiseer voorraad" },
          { name: "Bouw & Techniek", example: "Snellere offertes, slimmere projectplanning, materiaalkostenoptimalisatie" },
          { name: "Zakelijke Dienstverlening", example: "Klantinzichten, geautomatiseerde rapportages, intelligente planning" },
          { name: "Zorg & Welzijn", example: "Patientenstroom optimalisatie, administratieve automatisering, compliance monitoring" },
          { name: "Productie & Industrie", example: "Kwaliteitsvoorspelling, supply chain intelligentie, onderhoudsplanning" },
          { name: "Horeca & Hospitality", example: "Bezettingsvoorspelling, dynamische prijsstelling, gasttevredenheidsanalyse" },
          { name: "Transport & Logistiek", example: "Routeoptimalisatie, vlootbeheer, real-time leveringstracking" },
          { name: "Agrarisch & Food", example: "Oogstvoorspelling, ketenoptimalisatie, kwaliteitsborging en traceerbaarheid" }
        ]
      }
    },
    about: {
      title: "Over Krypton9",
      subtitle: "Uw AI Transformatie Partner",
      description: "Krypton9 is gespecialiseerd in het brengen van geavanceerde Agentic AI-oplossingen naar Nederlandse kleine en middelgrote bedrijven. We begrijpen de unieke uitdagingen van de MKB-sector en bieden praktische, winstgevende AI-strategieën die werken.",
      stats: [
        { value: "50+", label: "Getransformeerde Bedrijven" },
        { value: "€2M+", label: "Gegenereerde Kostenbesparingen" },
        { value: "95%", label: "Klanttevredenheid" }
      ]
    },
    resources: {
      title: "Gratis Bronnen",
      subtitle: "Download onze gidsen en whitepapers",
      items: [
        {
          title: "AI Strategiegids voor MKB",
          description: "Complete gids voor het ontwikkelen van uw AI-strategie",
          format: "PDF",
          file: "/api/static/resources/ai_strategy_guide_nl.pdf"
        },
        {
          title: "ROI Calculator",
          description: "Bereken uw potentiële AI-investeringsrendement",
          format: "Excel",
          file: "/api/static/resources/roi_calculator_nl.xlsx"
        },
        {
          title: "Casestudy Collectie",
          description: "Echte succesverhalen van Nederlandse bedrijven",
          format: "PDF",
          file: "/api/static/resources/case_studies_nl.pdf"
        }
      ]
    },
    contact: {
      title: "Laten We Praten Over Uw AI-Reis",
      subtitle: "Plan een gratis consultatie om te bespreken hoe AI uw bedrijf kan transformeren",
      form: {
        name: "Volledige Naam",
        email: "E-mailadres",
        company: "Bedrijfsnaam",
        phone: "Telefoonnummer",
        message: "Vertel ons over uw behoeften",
        submit: "Plan Consultatie",
        success: "Bedankt! We nemen binnen 24 uur contact met u op.",
        error: "Er is iets misgegaan. Probeer het opnieuw."
      }
    },
    newsletter: {
      title: "Blijf Op de Hoogte",
      subtitle: "Ontvang de laatste AI-inzichten en nieuwsberichten",
      placeholder: "Voer uw e-mail in",
      button: "Abonneren",
      success: "Succesvol geabonneerd!",
      error: "Voer een geldig e-mailadres in"
    },
    footer: {
      tagline: "Nederlands MKB transformeren met Agentic AI",
      links: {
        privacy: "Privacybeleid",
        terms: "Servicevoorwaarden",
        cookies: "Cookiebeleid"
      },
      copyright: "© 2025 Krypton9. Alle rechten voorbehouden."
    },
    chat: {
      greeting: "Hallo! Hoe kunnen we u vandaag helpen?",
      placeholder: "Typ uw bericht..."
    },
    auth: {
      welcomeBack: "Welkom Terug",
      signInSubtitle: "Log in om toegang te krijgen tot je portal",
      emailLabel: "E-mailadres",
      emailPlaceholder: "jij@bedrijf.nl",
      passwordLabel: "Wachtwoord",
      signIn: "Inloggen",
      signingIn: "Bezig met inloggen...",
      noAccount: "Nog geen account?",
      createAccount: "Account Aanmaken",
      createTitle: "Maak je Account Aan",
      createSubtitle: "Begin je AI-transformatie reis",
      fullNameLabel: "Volledige Naam",
      fullNamePlaceholder: "Jan Jansen",
      companyLabel: "Bedrijfsnaam",
      companyPlaceholder: "Jouw Bedrijf B.V.",
      creatingAccount: "Account aanmaken...",
      alreadyAccount: "Heb je al een account?",
      loginFailed: "Inloggen mislukt",
      registerFailed: "Registratie mislukt"
    }
  }
};

export const images = {
  hero: "https://static.prod-images.emergentagent.com/jobs/df67758f-954d-4451-a0a4-bbe474ea0355/images/b3b1b56cf9a416354e3f576447e4ac8ec7714c1bb0d1d71cbc9dfcf318e4c298.png",
  emma_analytics: "https://static.prod-images.emergentagent.com/jobs/df67758f-954d-4451-a0a4-bbe474ea0355/images/4bea96bb842a3d4fabc6c53f27de43155dc5bd021fea63240a06d0f1f59b60d6.png",
  emma_teamwork: "https://static.prod-images.emergentagent.com/jobs/df67758f-954d-4451-a0a4-bbe474ea0355/images/305b2878741910cc051797abae68c45de7145c52359d80673fa2729512caa316.png",
  emma_innovation: "https://static.prod-images.emergentagent.com/jobs/df67758f-954d-4451-a0a4-bbe474ea0355/images/576144b7713246c345c9014c88cea5b268e37e14249d07cf70489ab9082b055b.png",
  services: [
    "https://images.pexels.com/photos/16037279/pexels-photo-16037279.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    "https://images.pexels.com/photos/7988792/pexels-photo-7988792.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
  ],
  about: "https://static.prod-images.emergentagent.com/jobs/df67758f-954d-4451-a0a4-bbe474ea0355/images/305b2878741910cc051797abae68c45de7145c52359d80673fa2729512caa316.png",
  resources: "https://static.prod-images.emergentagent.com/jobs/df67758f-954d-4451-a0a4-bbe474ea0355/images/576144b7713246c345c9014c88cea5b268e37e14249d07cf70489ab9082b055b.png"
};
